import { Box, Sheet, Stack } from "@mui/joy";
import {
  ConceptKeyType,
  ConceptKeyType_automaticThought,
  ConceptType,
  coreBeliefKeyMapping,
  CoreBeliefKeyType,
  intermediateBeliefKeyMapping,
  IntermediateBeliefKeyType,
} from "api/conceptApi";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { getCopingStrategyStateFromData } from "hooks/getCopingStrategyState";
import ThoughtMapCard from "./ThoughtMapCard";
import { ThoughtMapText } from "./ThoughtMapText";
import AutomaticThought from "./AutomaticThought";
import BeliefTemplete from "./BeliefTemplate";

export default function ThoughtMap({
  data,
  from,
  to,
  fixedValue,
}: {
  data: ConceptType;
  from?: ConceptKeyType;
  to?: ConceptKeyType;
  fixedValue?: { [key in ConceptKeyType]?: string };
}) {
  const { t: t_thoughtmap } = useTranslation("translation", {
    keyPrefix: "thoughtmap",
  });

  const keyList = [
    "coreExperience",
    "coreBelief",
    "intermediateBelief",
    "copingStrategy",
    "automaticThought",
  ];

  const visibleKeyList = keyList.filter(
    (each, index) =>
      (!from || index >= keyList.findIndex((element) => element === from)) &&
      (!to || index <= keyList.findIndex((element) => element === to))
  );

  const automaticThoughtKeyList: ConceptKeyType_automaticThought[] =
    Object.keys(data)
      .filter((element) => element.startsWith("automaticThought"))
      .map((each) => each as ConceptKeyType_automaticThought);

  const coreExperienceAData = [
    ...(visibleKeyList.includes("coreExperience")
      ? [
          {
            id: "coreExperience",
            label: t_thoughtmap("핵심 경험"),
            placeholder: t_thoughtmap("지금의 나를 만든 과거의 경험은?"),
            content: data?.coreExperience,
          },
        ]
      : []),
    // ...(visibleKeyList.includes("coreBelief")
    //   ? [
    //       {
    //         id: "coreBelief",
    //         label: t_thoughtmap("핵심 믿음"),
    //         placeholder: fixedValue?.coreBelief
    //           ? fixedValue?.coreBelief
    //           : t_thoughtmap("내 마음속 깊이 자리 잡은 생각은?"),
    //         content: fixedValue?.coreBelief
    //           ? fixedValue?.coreBelief
    //           : Object.values(data?.coreBelief || {}).join("\n\n"),
    //       },
    //     ]
    //   : []),
  ];

  const copingStrategyState = getCopingStrategyStateFromData(data);

  const [copingStrategyList, setCopingStrategyList] = useState([
    ...(copingStrategyState?.hasSurrender ? ["굴복"] : []),
    ...(copingStrategyState?.hasOvercompensation ? ["과잉보상"] : []),
    ...(copingStrategyState?.hasAvoidance ? ["회피"] : []),
  ]);

  const [currentCopingStrategy, setCurrentCopingStrategy] = useState<string>(
    copingStrategyList[0]
  );

  useEffect(() => {
    const newCopingStrategyState = getCopingStrategyStateFromData(data);
    const newList = [
      ...(newCopingStrategyState?.hasSurrender ? ["굴복"] : []),
      ...(newCopingStrategyState?.hasOvercompensation ? ["과잉보상"] : []),
      ...(newCopingStrategyState?.hasAvoidance ? ["회피"] : []),
    ];
    if (!_.isEqual(copingStrategyList, newList)) {
      setCopingStrategyList(newList);
      setCurrentCopingStrategy(newList[0]);
    }
  }, [data]);

  useEffect(() => {
    if (fixedValue?.copingStrategy) {
      setCurrentCopingStrategy(fixedValue?.copingStrategy);
    }
  }, [fixedValue]);

  function getCopingStrategyIndex(copingStrategy: string) {
    return copingStrategyList.findIndex(
      (element) => element === copingStrategy
    );
  }
  const goNextCopingStrategy = useCallback(() => {
    if (
      getCopingStrategyIndex(currentCopingStrategy) <
      copingStrategyList.length - 1
    ) {
      setCurrentCopingStrategy(
        copingStrategyList[getCopingStrategyIndex(currentCopingStrategy) + 1]
      );
    } else {
      setCurrentCopingStrategy(copingStrategyList[0]);
    }
  }, [currentCopingStrategy]);

  const goPreviousCopingStrategy = useCallback(() => {
    if (getCopingStrategyIndex(currentCopingStrategy) > 0) {
      setCurrentCopingStrategy(
        copingStrategyList[getCopingStrategyIndex(currentCopingStrategy) - 1]
      );
    } else {
      setCurrentCopingStrategy(
        copingStrategyList[copingStrategyList.length - 1]
      );
    }
  }, [currentCopingStrategy]);

  return (
    <Sheet
      sx={{
        height: "100%",
        backgroundColor: "transparent",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {coreExperienceAData.map(({ id, label, content, placeholder }, index) => (
        <Stack key={id} spacing={0} alignItems="center" sx={{ p: 0 }}>
          <ThoughtMapCard
            label={label}
            content={
              <ThoughtMapText
                text={
                  (content && content?.trim().length > 0
                    ? content
                    : placeholder) as string
                }
                dimmed={!content || content?.trim().length === 0}
              />
            }
            labelBackgroundColor={
              content && content?.trim().length > 0
                ? "primary.solid"
                : "primary.deactivated"
            }
          />
        </Stack>
      ))}
      {visibleKeyList.includes("coreBelief") && (
        <BeliefTemplete
          title={t_thoughtmap("핵심 믿음")}
          placeholder={
            fixedValue?.coreBelief
              ? fixedValue?.coreBelief
              : t_thoughtmap("내 마음속 깊이 자리 잡은 생각은?")
          }
          data={Object.entries(data?.coreBelief || {})
            .filter(([label, value]) => value?.length > 0)
            .map(([label, value]) => ({
              label: coreBeliefKeyMapping[label as CoreBeliefKeyType],
              value: value,
            }))}
          hasConnectionLine={visibleKeyList.includes("coreExperience")}
        />
      )}
      {visibleKeyList.includes("intermediateBelief") && (
        <BeliefTemplete
          title={t_thoughtmap("중간 믿음")}
          placeholder={
            t_thoughtmap("오늘날 내가 삶을 해쳐나가는 방식은?") as string
          }
          data={Object.entries(data?.intermediateBelief || {})
            .filter(([label, value]) => value?.length > 0)
            .map(([label, value]) => ({
              label:
                intermediateBeliefKeyMapping[
                  label as IntermediateBeliefKeyType
                ],
              value: value,
            }))}
          hasConnectionLine={visibleKeyList.includes("coreBelief")}
        />
      )}
      {/* {visibleKeyList.includes("copingStrategy") && (
        <>
          {coreExperienceAData.length > 0 && (
            <Box
              sx={{ backgroundColor: "background.level3" }}
              className="w-[2px] h-[20px] mx-auto"
            />
          )}
          <CopingStrategy
            data={data}
            currentCopingStrategy={currentCopingStrategy}
            goNext={goNextCopingStrategy}
            goPrevious={goPreviousCopingStrategy}
            noArrow={
              copingStrategyList.length === 1 || !!fixedValue?.copingStrategy
            }
          />
        </>
      )} */}

      {visibleKeyList.includes("automaticThought") && (
        <>
          <Box
            sx={{ backgroundColor: "background.level3" }}
            className="w-[2px] h-[20px] mx-auto"
          />
          <AutomaticThought
            data={data}
            // idList={automaticThoughtKeyList.filter((element) =>
            //   !data
            //     ? true
            //     : !currentCopingStrategy ||
            //       data[element as ConceptKeyType_automaticThought]
            //         ?.copingStrategy === currentCopingStrategy
            // )}
            idList={automaticThoughtKeyList}
          />
        </>
      )}
    </Sheet>
  );
}
